// Matomo analytics utility compatible with Next.js 15
declare global {
  interface Window {
    _paq: [string, ...unknown[]][];
  }
}

export interface MatomoConfig {
  url: string;
  siteId: string;
  jsTrackerFile?: string;
  phpTrackerFile?: string;
}

export function initMatomo(config: MatomoConfig): void {
  if (typeof window === 'undefined') {
    return; // Don't run on server
  }

  const {
    url,
    siteId,
    jsTrackerFile = 'matomo.js',
    phpTrackerFile = 'matomo.php',
  } = config;

  window._paq = window._paq || [];

  // Require consent before tracking — consent is granted
  // via useCookieConsent hook when user accepts cookies
  window._paq.push(['requireConsent']);

  const u = url.endsWith('/') ? url : `${url}/`;
  window._paq.push(
    ['setTrackerUrl', `${u}${phpTrackerFile}`],
    ['setSiteId', siteId],
    ['trackPageView'],
    ['enableLinkTracking']
  );

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.defer = true;
  // eslint-disable-next-line scanjs-rules/assign_to_src
  script.src = `${u}${jsTrackerFile}`;

  const firstScript = document.querySelectorAll('script')[0];
  firstScript.parentNode?.insertBefore(script, firstScript);
}

let previousUrl =
  typeof window !== 'undefined' ? window.location.href : undefined;

export function trackPageView(url?: string): void {
  if (typeof window === 'undefined' || !window._paq) {
    return;
  }

  let currentUrl = url ?? window.location.href;
  if (currentUrl.startsWith('/')) {
    currentUrl = `${window.location.origin}${currentUrl}`;
  }

  if (previousUrl && previousUrl !== currentUrl) {
    window._paq.push(['setReferrerUrl', previousUrl]);
  }
  window._paq.push(['setCustomUrl', currentUrl]);
  window._paq.push(['setDocumentTitle', document.title]);
  window._paq.push(['trackPageView']);

  previousUrl = currentUrl;
}

export function trackEvent(
  category: string,
  action: string,
  name?: string,
  value?: number
): void {
  if (typeof window === 'undefined' || !window._paq) {
    return;
  }

  const params = [category, action, name, value].filter(
    (param) => param !== undefined
  );
  window._paq.push(['trackEvent', ...params]);
}
